<template>
  <BuilderWrapper
    v-if="showOnDataVisualisationOrConsolidatedCustomVisualisation"
    title="General Settings"
    helpful-tip="These settings apply across the entire app."
    data-test="general-settings"
  >
    <LabelledInput
      label="Organisation Name"
      helpful-tip="Displayed in the default page title"
    >
      <b-input
        v-model="orgName"
        placeholder="Your organisation name"
        data-test="input-org-name"
      />
    </LabelledInput>

    <LabelledInput
      label="Application Name"
      helpful-tip="Displayed in the default page title"
    >
      <b-input
        v-model="appName"
        placeholder="Your application name"
        data-test="input-app-name"
      />
    </LabelledInput>

    <LabelledInput
      v-if="!exclude.includes('logo')"
      label="Logo"
      helpful-tip="Displayed at the top of the sidebar and used as page thumbnail for social sharing"
    >
      <ImageUpload :config-path="['appConfig', 'logo']" />
    </LabelledInput>

    <template v-if="isVisualisationApp">
      <LabelledInput
        label="Total String Label"
        helpful-tip="Customise the text used to label the total"
        data-test="labelled-input-total-label"
      >
        <b-input
          v-model="totalStringLabel"
          :placeholder="TOTAL_STRING_LABEL"
          data-cy="total-string-label-input"
          data-test="input-total-label"
        />
      </LabelledInput>

      <SelectProjectLabelField data-test="select-label-field" />
    </template>
  </BuilderWrapper>
</template>

<script lang="ts">

import Vue, { PropType } from 'vue'
import Component from 'vue-class-component'

import { TOTAL_STRING_LABEL, APP_CONFIG_KEY } from '@/constants'
import BuilderWrapper from '@/components/builder/UI/BuilderWrapper.vue'
import LabelledInput from '@/components/builder/UI/LabelledInput.vue'
import ImageUpload from '@/components/builder/images/ImageUpload.vue'
import SelectProjectLabelField from '@/components/builder/GeneralSettings/SelectProjectLabelField.vue'
import { AppConfig } from '@/types'

const components = {
  BuilderWrapper,
  LabelledInput,
  ImageUpload,
  SelectProjectLabelField,
}

const props = {
  exclude: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
}

@Component({ components })
export default class GeneralSettings extends Vue.extend({ props }) {
  TOTAL_STRING_LABEL = TOTAL_STRING_LABEL

  get isCustom () {
    return this.$typedStore.activeVisualisation.isCustom
  }

  get isVisualisationApp () {
    return this.$typedStore.primary.app.isVisualisationApp
  }

  get isConsolidatedApp () {
    return this.$typedStore.primary.app.isConsolidatedApp
  }

  get showOnConsolidatedCustomVisualisationsOnly () {
    return this.isConsolidatedApp && this.isCustom
  }

  get showOnDataVisualisationOrConsolidatedCustomVisualisation () {
    return this.showOnConsolidatedCustomVisualisationsOnly || this.isVisualisationApp
  }

  get appName () {
    return this.$typedStore.primary.app.config.appConfig.appName
  }

  set appName (value) {
    this.updateConfig(value, 'appName')
  }

  get orgName () {
    return this.$typedStore.primary.app.config.appConfig.orgName
  }

  set orgName (value) {
    this.updateConfig(value, 'orgName')
  }

  get totalStringLabel () {
    return this.$typedStore.primary.app.config.appConfig.totalStringLabel ?? TOTAL_STRING_LABEL
  }

  set totalStringLabel (value) {
    this.updateConfig(value, 'totalStringLabel')
  }

  updateConfig (value: string, ...args: [keyof Pick<AppConfig, 'appName' | 'orgName' | 'totalStringLabel'>]) {
    this.$typedStore.primary.app.updateConfig({
      configPath: [APP_CONFIG_KEY, ...args],
      value,
    })
  }
}

</script>
